import { Socket } from 'socket.io-client';
import Colors from '~/constants/Colors';
import { BattleDispatcher } from '~/flux/dispatchers';
import GameStateStore from '~/flux/stores/GameStateStore/GameStateStore';
import messages from '~/flux/stores/GameStateStore/messages';
import InnStore from '~/flux/stores/InnStore/InnStore';
import mutationCreateInnBattle from '~/graphql/inn/mutationCreateInnBattle';
import mutationStartInnBattle from '~/graphql/inn/mutationStartInnBattle';
import { logError } from '~/logger';
import text from '~/text';

export default async function startInnAttackBattle(
  deps: {
    playerId: string;
    getAttackLoadout: () => unknown;
    getOpponentToAttack: () => { _id: string; player_inn_submode_state: { rank: number } } | null;
    _socket: Socket;
  },
  action: { game_submode: string }
) {
  const { playerId, getAttackLoadout, getOpponentToAttack, _socket } = deps;
  const { game_submode } = action;

  const attack_loadout = getAttackLoadout();
  const opponent_to_attack = getOpponentToAttack();
  if (!attack_loadout || !opponent_to_attack) {
    logError(new Error('startAttackBattle: missing attack_loadout or opponent_to_attack'), {
      module: 'InnStore',
      func: 'startAttackBattle',
    });
    InnStore.emit(InnStore.ATTACK_BATTLE_START_FAILED);
    BattleDispatcher.handleBattleAction({
      actionType: BattleDispatcher.CLEAR_PENDING_BATTLE_INIT,
    });
    return;
  }

  let _battle_id: string | undefined;
  try {
    // Stage 1: create battle (locks, state updates, pending payload in Redis; no engine init)
    const { battle_id } = await mutationCreateInnBattle({
      playerId,
      gameSubmode: game_submode,
      attackLoadout: attack_loadout,
      opponentId: opponent_to_attack._id,
      opponentRank: opponent_to_attack.player_inn_submode_state.rank,
    });
    _battle_id = battle_id;

    // Stage 2: socket joins the battle
    _socket.emit('join_battle', { battle_id });

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        _socket.off('battle_joined', handler);
        reject(new Error('join_battle timeout'));
      }, 10000);
      const handler = (data: { battle_id: string }) => {
        if (data.battle_id === battle_id) {
          clearTimeout(timeout);
          _socket.off('battle_joined', handler);
          resolve();
        }
      };
      _socket.on('battle_joined', handler);
    });

    // Stage 3: initialize the battle engine
    await mutationStartInnBattle({ playerId, battleId: battle_id });
  } catch (err) {
    const message = (err as Error).message;
    console.log({ err, message });
    const illegalKeys = [
      'inn_embattled',
      'inn_opponent_embattled',
      'inn_opponent_rank_changed',
      'no_inn_challenges_left',
      'inn_not_same_tourney',
      'inn_tournament_busy',
      'inn_tournament_missing',
    ];
    if (illegalKeys.includes(message)) {
      (window as any).$addMessageLogMessage(text(`server.illegal.${message}`), Colors.RED);
      GameStateStore.emit(messages.ILLEGAL_GAME_STATE_UPDATE, { message });
    } else {
      (window as any).$addMessageLogMessage(text('ui.server_error') || message, Colors.RED);
    }
    logError(err as Error, {
      module: 'InnStore',
      func: 'startAttackBattle',
      playerId,
      game_submode,
    });
    if (_battle_id != null) {
      _socket.emit('leave_battle', {
        battle_id: _battle_id,
      });
    }
    InnStore.emit(InnStore.ATTACK_BATTLE_START_FAILED);
    BattleDispatcher.handleBattleAction({
      actionType: BattleDispatcher.CLEAR_PENDING_BATTLE_INIT,
    });
  }
}
